import { Archive, ArchiveRestore } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatDateTimeLocale } from '@/common/utils/date';
import { FEATURES } from '@/config/features';
import type { Tramite } from '@/types';

interface ArchivadoBannerProps {
  tramite: Pick<Tramite, 'archivado' | 'archivado_motivo' | 'archivado_at' | 'archivado_por'>;
  /** Si se pasa, se renderiza el botón "Desarchivar" a la derecha. */
  onDesarchivar?: () => void;
  isSubmitting?: boolean;
  className?: string;
}

export function ArchivadoBanner({ tramite, onDesarchivar, isSubmitting, className }: ArchivadoBannerProps) {
  // Con la feature apagada no se muestra: no habría forma de desarchivar.
  if (!FEATURES.archivadoEscrituras || !tramite.archivado) return null;

  const auditoriaParts = [
    tramite.archivado_por?.name ? `Archivó ${tramite.archivado_por.name}` : null,
    tramite.archivado_at ? formatDateTimeLocale(tramite.archivado_at) : null,
  ].filter(Boolean);

  return (
    // Colores neutros: archivar es guardar, no un problema que haya que resolver.
    <div
      className={`flex items-start justify-between gap-3 rounded-md border bg-muted px-3 py-2 text-muted-foreground ${className ?? ''}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1 font-medium text-foreground">
          <Archive className="h-3.5 w-3.5" /> Escritura archivada
        </div>
        {tramite.archivado_motivo && (
          <p className="mt-1 whitespace-pre-wrap break-words text-xs">{tramite.archivado_motivo}</p>
        )}
        {auditoriaParts.length > 0 && (
          <p className="mt-1 text-xs opacity-80">{auditoriaParts.join(' · ')}</p>
        )}
      </div>
      {onDesarchivar && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          disabled={isSubmitting}
          onClick={onDesarchivar}
        >
          <ArchiveRestore className="h-3.5 w-3.5" /> Desarchivar
        </Button>
      )}
    </div>
  );
}

export default ArchivadoBanner;
